import { useTranslation } from 'react-i18next';

import {
    type BioscopeDataQuality,
    type FeedingDashboardConfig,
    type FeedingDataSources,
    type FeedingPen,
    warningTargetOf,
} from '@/domain/feedingDashboard';

import { readyPenHas, surfaceQuality } from './bioscopeQuality';
import { formatDashboardDay } from './dashboardDay';
import { PenBarChart } from './PenBarChart';
import { penBarValues } from './penBarValues';
import { systemLabels } from './systemLabel';

interface PenComparisonChartsProps {
    pens: FeedingPen[];
    /** Unique label per pen id (`penDisplayNames` over the period's pens); see penBarValues. */
    penNames?: ReadonlyMap<number, string>;
    /** Site config: the reference lines and the system name in the labels. */
    config: FeedingDashboardConfig;
    /** Site-wide source states, so a series that cannot have values says why. */
    dataSources: FeedingDataSources;
    /** The day the bars' closing-day values are read on, named in the empty state. */
    closingDay: string | null;
    /** Site-wide Bioscope state, narrowed per chart to that chart's own values. */
    bioscopeDataQuality: BioscopeDataQuality;
}

/** The bFCR (IPS 2.4, statement 21) and RGI (IPS 2.5, statement 22) charts per pen. */
export function PenComparisonCharts(props: PenComparisonChartsProps) {
    const { pens, penNames, config, dataSources, closingDay, bioscopeDataQuality } = props;
    const { t } = useTranslation();
    // IPS 40: every label that names the planning system uses the configured name.
    const system = systemLabels(t, config.pmsSystemName);
    // In the picker's format, so the caption names the day as the picker above shows it.
    const day = closingDay ? formatDashboardDay(closingDay) : null;

    // Both RGI values divide by Weight plan, so without that source the chart can never
    // fill; saying so keeps an empty chart from reading as a pen problem. Weight actual
    // blocks only the planning-system series, and the plan caption already covers it.
    const rgiNotes: string[] = [];
    if (dataSources.weightPmsPlan === 'notConfigured') {
        rgiNotes.push(t('feedingModule.charts.rgiNeedsWeightPlan', { system: system.name }));
    } else if (dataSources.weightPmsActual === 'notConfigured') {
        rgiNotes.push(t('feedingModule.charts.rgiNeedsWeightActual', { system: system.name }));
    }
    const fcrNotes =
        dataSources.fcrPms === 'notConfigured'
            ? [t('feedingModule.charts.fcrNotConfigured', { system: system.name })]
            : [];

    return (
        <>
            <PenBarChart
                title={t('feedingModule.charts.fcrTitle', { system: system.name })}
                axisLabel={t('feedingModule.kpi.fcrBio')}
                systemName={system.name}
                seriesLabels={{ pms: system.fcr, bioscope: t('feedingModule.charts.fcrBioscope') }}
                values={penBarValues(pens, 'fcr', penNames)}
                // The target at the precision the bars are judged against, so a site
                // target of 1.125 draws and reads as the 1.13 the table names.
                target={warningTargetOf(config, 'fcr').target}
                metric="fcr"
                decimals={2}
                noDataMessage={
                    day
                        ? t('feedingModule.charts.fcrNoData', { day })
                        : t('feedingModule.charts.fcrNoDataNoDay')
                }
                notes={fcrNotes}
                quality={surfaceQuality(
                    bioscopeDataQuality,
                    readyPenHas(pens, (pen) => pen.fcrBioscope)
                )}
            />
            <PenBarChart
                title={t('feedingModule.charts.rgiTitle', { system: system.name })}
                axisLabel={t('feedingModule.kpi.rgi')}
                systemName={system.name}
                seriesLabels={{ pms: system.rgi, bioscope: t('feedingModule.charts.rgiBioscope') }}
                values={penBarValues(pens, 'rgi', penNames)}
                target={warningTargetOf(config, 'rgi').target}
                metric="rgi"
                // The API floors RGI to one decimal; showing fewer would round 99.6 up to
                // a "100" label on a bar coloured as below target.
                decimals={1}
                noDataMessage={
                    day
                        ? t('feedingModule.charts.rgiNoData', { day })
                        : t('feedingModule.charts.rgiNoDataNoDay')
                }
                notes={rgiNotes}
                quality={surfaceQuality(
                    bioscopeDataQuality,
                    readyPenHas(pens, (pen) => pen.rgiBioscope)
                )}
            />
        </>
    );
}
